import { Box, Grid } from '@material-ui/core';
import * as d3 from 'd3';
import * as React from 'react';
import { AnalysisChartLoading } from '../../../AnalysisChartLoading';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../../AnalysisList';
import { internationalText } from '../../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../../DataTimedOut';
import { NotEnoughData } from '../../NotEnoughData';

import PercentageBarItem from './PercentageBarItem';

export interface TopCombinationsProps extends ChartProps {}

const TopCombinations: React.FC<TopCombinationsProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showSerpOnly: true, showLimitItems: true },
      })),
    []
  );

  // Fetch data
  const keywordQuery = projectsApiService.queries.analysis.useGetTopCombinationsByKeywordCount({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    limitItems: analysisFilterState?.limitItems,
  });

  const keywordData = keywordQuery?.result;
  const keywordLoading = keywordQuery?.query?.loading;

  const volumeQuery = projectsApiService.queries.analysis.useGetTopCombinationsByVolume({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    limitItems: analysisFilterState?.limitItems,
  });

  const volumeLoading = volumeQuery?.query?.loading;
  const volumeData = volumeQuery?.result;

  // Render
  if (didQueryTimeout(volumeQuery?.query)) {
    return <DataTimedOut />;
  }

  if (volumeLoading || keywordLoading) {
    return <AnalysisChartLoading />;
  }

  if (
    !volumeData?.combinationAnalyses?.length &&
    !keywordData?.combinationAnalyses?.length &&
    project.tagGroups.length < 2
  ) {
    return <NotEnoughData />;
  }

  const fontFamilyClass =
    analysisFilterState?.theme === 'voxel' ? 'font-voxel-charts' : 'font-pixel-charts';
  return (
    <div data-screenshot-id="chart-loaded">
      <div className="min-h-[500px] pt-4">
        <Grid container justifyContent={'space-between'}>
          <Grid container item justifyContent="center" xs={6}>
            <Grid item xs={10}>
              <div className={`font-medium ${fontFamilyClass} text-base`}>
                {internationalText.chart.topic_combinations.keyword_description[language]} (
                {d3.format('.2s')(keywordData?.projectKeywordCount)})
              </div>
            </Grid>
            <Grid item xs={10}>
              <div className="py-3">
                {keywordData?.combinationAnalyses?.map((c, index) => (
                  <div key={index}>
                    <PercentageBarItem
                      name={c.combinationName}
                      value={c.keywordCount}
                      total={keywordData?.projectKeywordCount}
                      totalRescaling={keywordData?.combinationAnalyses[0]?.keywordCount}
                      palette={palette}
                    />
                  </div>
                ))}
              </div>
            </Grid>
          </Grid>
          <Grid container item justifyContent="center" xs={6}>
            <Grid item xs={10}>
              <div className={`font-medium ${fontFamilyClass} text-base`}>
                {internationalText.chart.topic_combinations.volume_description[language]} (
                {d3.format('.2s')(volumeData?.projectSearchVolume)})
              </div>
            </Grid>
            <Grid item xs={10}>
              <div className="py-3">
                {volumeData?.combinationAnalyses?.map((c, index) => (
                  <div key={index}>
                    <PercentageBarItem
                      name={c.combinationName}
                      value={c.searchVolume}
                      total={volumeData?.projectSearchVolume}
                      palette={palette}
                      totalRescaling={volumeData?.combinationAnalyses[0]?.searchVolume}
                    />
                  </div>
                ))}
              </div>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default TopCombinations;
